import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PencilIcon, MapPinIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import EditAnggotaModal from "./AnggotaModal";
import toast, { Toaster } from "react-hot-toast";
import { rtdb } from "../../../config/firebase";
import { ref, onValue, remove } from "firebase/database";

const siteOptions = [
  { id: "jayapura", label: "Jayapura" },
  { id: "manokwari", label: "Manokwari" },
];

// Card anggota dengan klik foto buka tab baru
const AnggotaCard = ({ anggota, onEdit, onDelete }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    transition={{ type: "spring", stiffness: 100 }}
    className="relative flex items-center gap-4 p-5 bg-white/60 backdrop-blur-xl rounded-2xl shadow-md border border-white/50 group"
  >
    <div className="flex-shrink-0">
      <a href={anggota.fotoUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={anggota.fotoUrl}
          alt={anggota.nama}
          className="h-20 w-20 rounded-full object-cover shadow-inner border-4 border-white ring-2 ring-gray-200 cursor-pointer hover:scale-105 transition-transform duration-200"
        />
      </a>
    </div>
    <div className="flex-grow">
      <p className="font-bold text-lg text-gray-900 leading-tight">{anggota.nama}</p>
      <p className="text-sm text-green-800 font-bold bg-green-100 px-2.5 py-1 inline-block rounded-full mt-1.5">
        {anggota.jabatan}
      </p>
    </div>
    <div className="flex-shrink-0 self-start flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={() => onEdit(anggota)}
        className="p-2.5 rounded-full hover:bg-yellow-100 text-yellow-600 transition-colors"
        title="Edit Anggota"
      >
        <PencilIcon className="w-5 h-5" />
      </button>
      <button
        onClick={() => onDelete(anggota.id, anggota.nama, anggota.fotoUrl)}
        className="p-2.5 rounded-full hover:bg-red-100 text-red-600 transition-colors"
        title="Hapus Anggota"
      >
        <TrashIcon className="w-5 h-5" />
      </button>
    </div>
  </motion.div>
);

export default function KelolaStrukturOrganisasi() {
  const [strukturOrganisasi, setStrukturOrganisasi] = useState({});
  const [editingAnggota, setEditingAnggota] = useState(null);
  const [selectedSite, setSelectedSite] = useState("jayapura");

  // 🔹 Load data dari Firebase node "Organisasi"
  useEffect(() => {
    const dbRef = ref(rtdb, "Organisasi");
    const unsubscribe = onValue(dbRef, (snapshot) => {
      const data = snapshot.val() || {};
      const grouped = {};
      Object.entries(data).forEach(([key, value]) => {
        const lokasi = value.lokasi || "jayapura";
        if (!grouped[lokasi]) grouped[lokasi] = [];
        grouped[lokasi].push({ id: key, ...value });
      });
      setStrukturOrganisasi(grouped);
    });
    return () => unsubscribe();
  }, []);

  const currentStruktur = strukturOrganisasi[selectedSite] || [];
  const totalAnggota = currentStruktur.length;

  const handleEditAnggotaOpen = (anggota) =>
    setEditingAnggota({ ...anggota, isNew: false });

  const handleAddAnggota = () => {
    setEditingAnggota({
      id: null,
      nama: "",
      jabatan: "",
      fotoUrl: "",
      lokasi: selectedSite,
      isNew: true,
    });
  };

  // 🔹 Hapus data dari node "Organisasi" dan juga file dari Storage
  const handleDeleteAnggota = async (id, nama) => {
    if (!window.confirm(`Yakin ingin menghapus ${nama}?`)) return;

    try {
      await remove(ref(rtdb, `Organisasi/${id}`));
      toast.success(`Anggota ${nama} berhasil dihapus.`);
    } catch (err) {
      console.error("❌ Gagal menghapus anggota:", err);
      toast.error(err.message || "Gagal menghapus data!");
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50">
      <Toaster position="top-right" reverseOrder={false} />
      <main className="relative z-10 p-4 md:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <motion.header
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="sticky top-4 z-20 bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 mb-8 p-4"
        >
          <div className="flex justify-between items-center flex-wrap gap-4">
            <h2 className="text-2xl font-bold text-gray-800">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-500">
                Kelola
              </span>{" "}
              Daftar Pimpinan
            </h2>

            <button
              onClick={handleAddAnggota}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-[1.03]"
            >
              <PlusIcon className="w-5 h-5" /> Tambah Anggota
            </button>
          </div>

          <p className="text-sm text-gray-600 mt-1 mb-4">
            Daftar pimpinan untuk lokasi yang dipilih ({totalAnggota} Anggota).
          </p>

          {/* Tabs Lokasi */}
          <div className="flex items-center gap-2 bg-white/50 border p-1 rounded-xl w-fit">
            {siteOptions.map((site) => (
              <button
                key={site.id}
                onClick={() => setSelectedSite(site.id)}
                className="relative px-4 py-1.5 text-sm font-semibold transition-all duration-300 flex items-center gap-2"
              >
                {selectedSite === site.id && (
                  <motion.div
                    layoutId="site-tab-underline"
                    className="absolute inset-0 bg-green-200/50 rounded-lg z-0 shadow-inner"
                  />
                )}
                <MapPinIcon
                  className={`w-4 h-4 relative z-10 ${
                    selectedSite === site.id ? "text-green-600" : "text-gray-500"
                  }`}
                />
                <span
                  className={`relative z-10 ${
                    selectedSite === site.id ? "text-green-800" : "text-gray-700"
                  }`}
                >
                  {site.label} ({(strukturOrganisasi[site.id] || []).length} Anggota)
                </span>
              </button>
            ))}
          </div>
        </motion.header>

        {/* Daftar Anggota */}
        <motion.div
          key={selectedSite}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-5 max-w-4xl mx-auto"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Daftar Pimpinan Lokasi:{" "}
            <span className="text-green-600">
              {siteOptions.find((s) => s.id === selectedSite)?.label}
            </span>
          </h3>

          <AnimatePresence mode="wait">
            {currentStruktur.length > 0 ? (
              currentStruktur.map((anggota) => (
                <AnggotaCard
                  key={anggota.id}
                  anggota={anggota}
                  onEdit={handleEditAnggotaOpen}
                  onDelete={handleDeleteAnggota}
                />
              ))
            ) : (
              <div className="text-center py-16 border-2 border-dashed rounded-xl text-gray-500 bg-white/60 backdrop-blur-lg">
                <p className="text-xl font-medium">
                  Daftar pimpinan untuk {siteOptions.find((s) => s.id === selectedSite)?.label} kosong.
                </p>
                <p className="mt-2 text-base">Silakan gunakan tombol "Tambah Anggota" di atas.</p>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      {/* Modal Edit */}
      <AnimatePresence>
        {editingAnggota && (
          <EditAnggotaModal
            anggota={editingAnggota}
            onSave={() => setEditingAnggota(null)}
            onClose={() => setEditingAnggota(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
